import TableContainer from "components/Common/TableContainer"
import React, { useEffect, useMemo, useState, useCallback } from "react"
import { useDebounce } from "hooks/useDebounce"
import { withRouter } from "react-router-dom"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import * as url from "../../helpers/url_helper"

import { Name, ShortDescription } from "./merchantCol"

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"

import {
  getMerchants as onGetMerchants,
  clearMerchants,
  deleteMerchant,
} from "store/merchants/actions"

//redux
import AccessControlWrapper from "components/Common/PermissionButton"
import { useDispatch, useSelector } from "react-redux"
import { PageTitle } from "constants/ui"
import IconButton from "components/Common/IconButton"
import CButton from "components/Common/CButton"
import ConfirmButton from "components/Common/ConfirmButton"
import { getFirstTabPermission } from "helpers/tab_helper"
import { calculatePageAfterAction, calculateStartIndex } from "helpers/pagination_helper"

const MerchantList = props => {
  //meta title
  document.title = PageTitle.MERCHANT_LIST

  const dispatch = useDispatch()
  const { history } = props

  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)

  const { data, state, userMenu } = useSelector(state => ({
    data: state.merchants,
    state: state,
    userMenu: state.userMenu?.userMenu || [],
  }))

  const firstTabPermission = getFirstTabPermission("MERCHANT_DETAIL", userMenu)


  const columns = useMemo(
    () => [
      {
        Header: "Logo",
        accessor: "logo",
        disableFilters: true,
        filterable: true,

        accessor: cellProps => (
          <>
            {!cellProps.logo ? (
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle">
                  {cellProps.legalEntityName.charAt(0)}
                </span>
              </div>
            ) : (
              <div>
                <img
                  className="rounded-circle avatar-xs"
                  src={cellProps.logo}
                  alt=""
                />
              </div>
            )}
          </>
        ),
      },
      {
        Header: "Display Name",
        accessor: "displayName",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => {
          return <ShortDescription {...cellProps} />
        },
      },
      {
        Header: "Legal Entity Name",
        accessor: "legalEntityName",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => {
          return <Name {...cellProps} />
        },
      },
      {
        Header: "Brand",
        accessor: "brand.name",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => {
          return cellProps.row.original.brand && cellProps.row.original.brand.name ? cellProps.row.original.brand.name : "-"
        },
      },
      {
        Header: "Legal Entity Address",
        accessor: "legalEntityAddress",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => {
          return <ShortDescription {...cellProps} />
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <div className="d-flex gap-2">
              <AccessControlWrapper
                actionType={firstTabPermission?.actionType || "get"}
                contextUri={firstTabPermission?.contextUri || url.GET_MERCHANTS}
                history={history}
              >
                <IconButton.View
                  onClick={() => {
                    const merchantID = cellProps.row.original.merchantID
                    history.push(`/merchants/${merchantID}`)
                  }}
                />
              </AccessControlWrapper>
              <ConfirmButton
                onConfirm={() =>
                  handleDeleteMerchant(
                    cellProps.row.original.merchantID
                  )
                }
                message="Are you sure you want to delete this Merchant? This action cannot be undone."
                renderButton={<IconButton.Delete />}
                contextUri={url.MANAGE_MERCHANT}
                actionType="post"
              ></ConfirmButton>
            </div>
          )
        },
      },
    ],
    []
  )

  // Load fresh merchants data when component mounts
  useEffect(() => {
    dispatch(
      onGetMerchants({
        headers: {
          "input-parameters": {},
        },
        pagination: { startIndex: 0 },
      })
    )
  }, [dispatch])

  // Clear merchants and search when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearMerchants())
    }
  }, [dispatch])

  const rowClick = (e, row) => {
    const {
      original: { merchantID },
    } = row
    history.push({
      pathname: `/merchants/${merchantID}`,
    })
  }

  function handleAddMerchantsClick() {
    history.push("/merchants/add-merchant")
  }

  const dispatchSearch = useCallback((value) => {
    setPage(1)
    dispatch(
      onGetMerchants({
        headers: {
          "input-parameters": { keyword: value || "" },
        },
        pagination: { startIndex: 0 },
      })
    )
  }, [dispatch])

  const [debouncedSearch] = useDebounce(dispatchSearch)

  const handleSearchChange = e => {
    const { value } = e.target
    setSearch(value)
    debouncedSearch(value)
  }

  const handlePageChange = page => {
    const startIndex = (page - 1) * 10 // Assuming page size is 10
    setPage(page)
    dispatch(
      onGetMerchants({
        headers: {
          "input-parameters": { keyword: search || "" },
        },
        pagination: { startIndex },
      })
    )
  }

  const handleRefresh = () => {
    dispatch(
      onGetMerchants({
        headers: {
          "input-parameters": { keyword: search || "" },
        },
        pagination: { startIndex: (page - 1) * 10 },
      })
    )
  }

  const handleDeleteMerchant = merchantID => {
    const targetPage = calculatePageAfterAction(page, data.merchants?.length || 0)
    dispatch(
      deleteMerchant(
        {
          merchantID: merchantID,
          actionType: "delete",
        },
        {
          refreshPagination: {
            startIndex: calculateStartIndex(targetPage),
            keyword: search || ""
          }
        }
      )
    )
    setPage(targetPage)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs
            title={PageTitle.MERCHANT_LIST}
            breadcrumbItem={PageTitle.MERCHANT_LIST}
          />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row>
                    <Col md="6">
                      <div>
                        <input
                          type="text"
                          placeholder="search merchants..."
                          onChange={handleSearchChange}
                          className="form-control w-50 rounded-pill"
                        />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="text-sm-end">
                        <AccessControlWrapper
                          actionType={"post"}
                          contextUri={
                            url.MANAGE_MERCHANT
                          }
                          history={history}
                        >
                          <CButton.Primary
                            icon="mdi mdi-plus-circle-outline"
                            text="Add Merchant"
                            className="mb-2 me-2"
                            onClick={handleAddMerchantsClick}
                          />
                        </AccessControlWrapper>
                      </div>
                    </Col>
                  </Row>

                  {/* Delete Error Alert */}
                  {data && data.deleteError && data.deleteError.message && (
                    <Row className="mb-3">
                      <Col lg="12">
                        <div className="alert alert-danger" role="alert">
                          <strong>Delete Error:</strong> {data.deleteError.message}
                        </div>
                      </Col>
                    </Row>
                  )}

                  <TableContainer
                    columns={columns}
                    data={data?.error?.message ? [] : data.merchants}
                    isGlobalFilter={false}
                    isAddUserList={true}
                    rowClick={rowClick}
                    protectedRowClick={true}
                    contextUri={url.GET_MERCHANTS}
                    actionType="get"
                    handleAddButtonClick={handleAddMerchantsClick}
                    customPageSize={10}
                    className="custom-header-css"
                    columnFilter={false}
                    changePageSize={false}
                    isAddButtonText="Add Merchant"
                    onPageChange={handlePageChange}
                    currentPage={page}
                    hasNextPage={!data?.error?.message && data.merchants?.length >= 10}
                    onRefresh={handleRefresh}
                    refreshContextUri={url.GET_MERCHANTS}
                    refreshActionType="get"
                    history={history}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(MerchantList)
